import { useEffect, useRef, useState } from "react";
import { Move } from "lucide-react";
import { api } from "../../lib/api";

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export function BalloonOverlay({ pageId, version, onCommit }) {
  const [g, setG] = useState(null);
  const [drag, setDrag] = useState(null);
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [g]);
  useEffect(() => { api.get(`/comic-pages/${pageId}/geometry`).then((r) => setG(r.data)).catch(() => setG(null)); }, [pageId, version]);
  if (!g) return null;
  const scale = () => (w || ref.current?.clientWidth || 0) / g.width;
  const start = (e, pi, b, mode) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ pi, i: b.i, mode, sx: e.clientX, sy: e.clientY, box: [...b.box], tail: b.tail ? [...b.tail] : null, kind: b.kind });
  };
  const move = (e) => {
    if (!drag) return;
    const k = scale();
    const dx = (e.clientX - drag.sx) / k, dy = (e.clientY - drag.sy) / k;
    setDrag({ ...drag, dx, dy });
  };
  const live = (pi, b) => {
    if (!drag || drag.pi !== pi || drag.i !== b.i || drag.dx === undefined) return { box: b.box, tail: b.tail };
    const [x0, y0, x1, y1] = g.panels[pi].rect;
    let [bx, by, bw, bh] = drag.box;
    let tail = drag.tail;
    if (drag.mode === "move") { bx = clamp(bx + drag.dx, x0, x1 - bw); by = clamp(by + drag.dy, y0, y1 - bh); if (tail) tail = [tail[0] + drag.dx, tail[1] + drag.dy]; }
    if (drag.mode === "resize") bw = clamp(bw + drag.dx, (x1 - x0) * 0.08, x1 - bx);
    if (drag.mode === "tail") tail = [drag.tail[0] + drag.dx, drag.tail[1] + drag.dy];
    return { box: [bx, by, bw, bh], tail };
  };
  const end = () => {
    if (!drag) return;
    const p = g.panels[drag.pi];
    const b = p.balloons.find((x) => x.i === drag.i);
    const { box, tail } = live(drag.pi, b);
    setDrag(null);
    if (drag.dx === undefined) return;
    const [x0, y0, x1, y1] = p.rect;
    const pw = x1 - x0, ph = y1 - y0;
    const upd = { x: clamp((box[0] - x0) / pw, 0, 1), y: clamp((box[1] - y0) / ph, 0, 1) };
    if (drag.mode === "resize") upd.w = clamp(box[2] / pw, 0.08, 1);
    if (tail && (drag.mode === "tail" || drag.mode === "move")) { upd.tx = clamp((tail[0] - x0) / pw, -0.2, 1.2); upd.ty = clamp((tail[1] - y0) / ph, -0.2, 1.2); }
    onCommit(p.id, drag.i, upd);
  };
  const k = scale();
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden" onPointerMove={move} onPointerUp={end} onPointerLeave={end} data-testid="balloon-overlay">
      {g.panels.map((p, pi) => p.balloons.map((b) => {
        const { box, tail } = live(pi, b);
        const id = `${pi}-${b.i}`;
        return (
          <div key={id}>
            <div onPointerDown={(e) => start(e, pi, b, "move")} className="absolute border-2 border-dashed border-amber-400/80 hover:bg-amber-400/10 cursor-move rounded group"
              style={{ left: box[0] * k, top: box[1] * k, width: box[2] * k, height: box[3] * k }} data-testid={`balloon-handle-${id}`}>
              <Move size={10} className="absolute -top-3 -left-3 text-amber-300 opacity-0 group-hover:opacity-100" />
              {b.kind !== "sfx" && <span onPointerDown={(e) => start(e, pi, b, "resize")} className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-6 bg-amber-400 rounded cursor-ew-resize" data-testid={`balloon-resize-${id}`} />}
            </div>
            {tail && ["speech", "thought"].includes(b.kind) && <span onPointerDown={(e) => start(e, pi, b, "tail")} className="absolute w-4 h-4 -ml-2 -mt-2 rounded-full bg-pink-500 border-2 border-white cursor-crosshair"
              style={{ left: tail[0] * k, top: tail[1] * k }} data-testid={`balloon-tail-${id}`} />}
          </div>
        );
      }))}
    </div>
  );
}
